import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PrintAgentService } from './print-agent.service';
import { StorageKeys } from './storage-keys';
import { StorageService } from './storage.service';
import {
  DurableOperationIdentity,
  DurableOperationIdentityService,
} from './durable-operation-identity.service';

export type DurablePrintRole = 'BILLING' | 'KOT';

export interface DurablePrintRequest {
  id: string;
  tenantId: string;
  deviceId: string;
  printerRole: DurablePrintRole;
  payload: Record<string, unknown>;
  labels: Record<string, unknown>;
  notBefore?: number;
}

interface PrintOutboxEntry {
  request: DurablePrintRequest;
  createdAt: number;
}

interface PrintOutboxState {
  version: 1;
  identity: DurableOperationIdentity | null;
  pending: PrintOutboxEntry[];
  acknowledgedIds: string[];
}

const INITIAL_RETRY_MS = 3_000;
const MAX_RETRY_MS = 60_000;
const MAX_PENDING = 100;
const MAX_ACKNOWLEDGED = 500;

/**
 * Small durable hand-off in front of PrintAgent.
 *
 * Both BILLING and KOT jobs are written in one Preferences update before the
 * first HTTP call. A 2xx means PrintAgent durably accepted the stable job id;
 * failures stay pending and are retried without creating another id.
 */
@Injectable({ providedIn: 'root' })
export class PrintOutboxService implements OnDestroy {
  private storageTail: Promise<void> = Promise.resolve();
  private flushInFlight: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryMs = INITIAL_RETRY_MS;

  constructor(
    private storage: StorageService,
    private printAgent: PrintAgentService,
    private identity: DurableOperationIdentityService,
  ) {}

  static stableJobId(
    tenantName: string,
    kioskCode: string,
    orderId: number,
    role: DurablePrintRole,
  ): string {
    const tenant = PrintOutboxService.identityPart(tenantName);
    const kiosk = PrintOutboxService.identityPart(kioskCode);
    if (!Number.isSafeInteger(orderId) || orderId <= 0) {
      throw new Error('PRINT_JOB_REQUIRES_ORDER_ID');
    }
    return `kiosk-print-v1|${tenant}|${kiosk}|${orderId}|${role}`;
  }

  async enqueueAndFlush(requests: DurablePrintRequest[]): Promise<void> {
    const normalized = requests.map((request) =>
      this.normalizeRequest(request),
    );
    if (normalized.length === 0) {
      return;
    }

    await this.withStorageLock(async () => {
      const state = await this.readState();
      const identity =
        state.identity || (await this.identity.captureCurrent());
      await this.identity.assertCurrent(identity);
      for (const request of normalized) {
        if (
          request.tenantId !== identity.tenantName ||
          request.deviceId !== `kiosk-${identity.kioskCode}`
        ) {
          throw new Error('PRINT_JOB_IDENTITY_MISMATCH');
        }
      }
      const pending = [...state.pending];
      for (const request of normalized) {
        if (state.acknowledgedIds.includes(request.id)) {
          continue;
        }
        const existing = pending.find(
          (entry) => entry.request.id === request.id,
        );
        if (existing) {
          if (
            this.printableCanonical(existing.request) !==
            this.printableCanonical(request)
          ) {
            throw new Error(`PRINT_JOB_ID_CONFLICT:${request.id}`);
          }
          continue;
        }
        pending.push({ request, createdAt: Date.now() });
      }
      if (pending.length > MAX_PENDING) {
        throw new Error(`PRINT_OUTBOX_FULL:${pending.length}`);
      }
      await this.persistVerified({ ...state, identity, pending });
    });
    await this.flushPending();
  }

  async resumePending(): Promise<void> {
    await this.flushPending();
  }

  async pendingCount(): Promise<number> {
    return (await this.readState()).pending.length;
  }

  ngOnDestroy(): void {
    this.cancelRetry();
  }

  private async flushPending(): Promise<void> {
    if (this.flushInFlight) {
      return this.flushInFlight;
    }
    this.flushInFlight = this.performFlush();
    try {
      await this.flushInFlight;
    } finally {
      this.flushInFlight = null;
    }
  }

  private async performFlush(): Promise<void> {
    const state = await this.readState();
    if (state.identity) {
      await this.identity.assertCurrent(state.identity);
    }
    const now = Date.now();
    let retryRequired = false;
    let earliestFuture: number | null = null;

    for (const entry of state.pending) {
      const notBefore = Number(entry.request.notBefore || 0);
      if (notBefore > now) {
        earliestFuture =
          earliestFuture === null
            ? notBefore
            : Math.min(earliestFuture, notBefore);
        continue;
      }
      try {
        const response = await firstValueFrom(
          this.printAgent.printOrder(entry.request),
        );
        if (this.isPrinterNotConfigured(response)) {
          const roleMissing = await this.isRoleMissingFromAgent(
            entry.request.printerRole,
          );
          if (!roleMissing) {
            throw response;
          }
        }
        await this.acknowledge(entry);
      } catch (error) {
        if (this.isPrinterNotConfigured(error)) {
          try {
            const roleMissing = await this.isRoleMissingFromAgent(
              entry.request.printerRole,
            );
            if (roleMissing) {
              console.warn(
                `Durable print ${entry.request.id} is not applicable: PrintAgent has no ${entry.request.printerRole} printer.`,
              );
              await this.acknowledge(entry);
              continue;
            }
          } catch (inventoryError) {
            console.error(
              `Unable to verify printer role ${entry.request.printerRole}; keeping ${entry.request.id} pending:`,
              inventoryError,
            );
          }
        }
        retryRequired = true;
        console.error(
          `Durable print ${entry.request.id} remains pending:`,
          error,
        );
      }
    }

    const remaining = (await this.readState()).pending.length;
    if (remaining === 0) {
      this.cancelRetry();
      this.retryMs = INITIAL_RETRY_MS;
      return;
    }

    const futureDelay =
      earliestFuture === null ? Number.POSITIVE_INFINITY : earliestFuture - now;
    const delay = retryRequired
      ? Math.min(this.retryMs, futureDelay)
      : futureDelay;
    this.scheduleRetry(Number.isFinite(delay) ? Math.max(50, delay) : this.retryMs);
    if (retryRequired) {
      this.retryMs = Math.min(this.retryMs * 2, MAX_RETRY_MS);
    }
  }

  private isPrinterNotConfigured(value: unknown): boolean {
    const candidate = value as any;
    return [
      candidate,
      candidate?.code,
      candidate?.error,
      candidate?.error?.code,
      candidate?.error?.error,
      candidate?.error?.message,
      candidate?.message,
    ].some(
      (part) =>
        typeof part === 'string' &&
        part.trim().toUpperCase() === 'PRINTER_NOT_CONFIGURED',
    );
  }

  private async isRoleMissingFromAgent(
    role: DurablePrintRole,
  ): Promise<boolean> {
    const printers = await firstValueFrom(this.printAgent.getPrinters());
    if (!Array.isArray(printers)) {
      throw new Error('INVALID_PRINT_AGENT_PRINTER_INVENTORY');
    }
    return !printers.some(
      (printer: any) =>
        `${printer?.role || ''}`.trim().toUpperCase() === role,
    );
  }

  private async acknowledge(entry: PrintOutboxEntry): Promise<void> {
    await this.withStorageLock(async () => {
      const state = await this.readState();
      const current = state.pending.find(
        (candidate) => candidate.request.id === entry.request.id,
      );
      if (!current) {
        return;
      }
      if (
        this.canonical(current.request) !== this.canonical(entry.request)
      ) {
        throw new Error(`PRINT_ACK_MISMATCH:${entry.request.id}`);
      }
      const acknowledgedIds = [
        ...state.acknowledgedIds.filter((id) => id !== entry.request.id),
        entry.request.id,
      ].slice(-MAX_ACKNOWLEDGED);
      await this.persistVerified({
        version: 1,
        identity: state.identity,
        pending: state.pending.filter(
          (candidate) => candidate.request.id !== entry.request.id,
        ),
        acknowledgedIds,
      });
    });
  }

  private scheduleRetry(delayMs: number): void {
    this.cancelRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flushPending();
    }, delayMs);
  }

  private cancelRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private async readState(): Promise<PrintOutboxState> {
    const stored = await this.storage.getItemStrict<unknown>(
      StorageKeys.PRINT_OUTBOX,
    );
    if (stored === null) {
      return {
        version: 1,
        identity: null,
        pending: [],
        acknowledgedIds: [],
      };
    }
    this.assertState(stored);
    if (stored.identity) {
      await this.identity.assertCurrent(stored.identity);
    }
    return this.clone(stored);
  }

  private async persistVerified(state: PrintOutboxState): Promise<void> {
    this.assertState(state);
    await this.storage.setItem(StorageKeys.PRINT_OUTBOX, state);
    const stored = await this.storage.getItemStrict<unknown>(
      StorageKeys.PRINT_OUTBOX,
    );
    if (this.canonical(stored) !== this.canonical(state)) {
      throw new Error('PRINT_OUTBOX_WRITE_FAILED');
    }
  }

  private normalizeRequest(
    request: DurablePrintRequest,
  ): DurablePrintRequest {
    const id = `${request?.id || ''}`.trim();
    const tenantId = `${request?.tenantId || ''}`.trim();
    const deviceId = `${request?.deviceId || ''}`.trim();
    if (
      !id ||
      id.length > 256 ||
      !tenantId ||
      !deviceId ||
      !['BILLING', 'KOT'].includes(request?.printerRole) ||
      !request?.payload ||
      typeof request.payload !== 'object' ||
      !request?.labels ||
      typeof request.labels !== 'object'
    ) {
      throw new Error('INVALID_DURABLE_PRINT_REQUEST');
    }
    return this.clone({
      ...request,
      id,
      tenantId,
      deviceId,
      notBefore: Math.max(0, Number(request.notBefore || 0)),
    });
  }

  private assertState(value: unknown): asserts value is PrintOutboxState {
    const state = value as any;
    if (
      !state ||
      state.version !== 1 ||
      !Array.isArray(state.pending) ||
      !Array.isArray(state.acknowledgedIds) ||
      (state.identity === null
        ? state.pending.length > 0 || state.acknowledgedIds.length > 0
        : (() => {
            try {
              this.identity.assertShape(state.identity);
              return false;
            } catch {
              return true;
            }
          })()) ||
      state.pending.length > MAX_PENDING ||
      state.acknowledgedIds.length > MAX_ACKNOWLEDGED ||
      state.acknowledgedIds.some((id: unknown) => typeof id !== 'string') ||
      state.pending.some(
        (entry: any) =>
          !entry ||
          !Number.isSafeInteger(entry.createdAt) ||
          !entry.request ||
          typeof entry.request.id !== 'string',
      )
    ) {
      throw new Error('INVALID_PRINT_OUTBOX');
    }
  }

  private withStorageLock<T>(action: () => Promise<T>): Promise<T> {
    let release!: () => void;
    const previous = this.storageTail;
    this.storageTail = new Promise<void>((resolve) => {
      release = resolve;
    });
    return previous.then(action).finally(release);
  }

  private canonical(value: unknown): string {
    return JSON.stringify(value, (_key, current) => {
      if (
        current &&
        typeof current === 'object' &&
        !Array.isArray(current)
      ) {
        return Object.keys(current)
          .sort()
          .reduce((sorted: Record<string, unknown>, key) => {
            sorted[key] = current[key];
            return sorted;
          }, {});
      }
      return current;
    });
  }

  private printableCanonical(request: DurablePrintRequest): string {
    const printable = this.clone(request);
    delete printable.notBefore;
    return this.canonical(printable);
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private static identityPart(value: string): string {
    const normalized = `${value || ''}`.trim();
    if (!normalized || normalized.length > 128 || /[\0\r\n|]/.test(normalized)) {
      throw new Error('INVALID_PRINT_IDENTITY');
    }
    return normalized;
  }
}
